/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import set from 'lodash/set';
import subtract from 'lodash/subtract';
import {
    CHANGE_ORDER_DETAILS_VIEW,
    CLEAR_GROUP_SIZE_NOTIFICATION,
    CLEAR_ORDER_DETAILS_ERROR,
    CLEAR_VALIDATION_ERRORS,
    CLOSE_PASS_CONFIRMATION_MODAL,
    COMPLETE_SUBMISSION_FAILURE,
    COMPLETE_SUBMISSION_SUCCESS,
    CONFIRM_PASS,
    CONTROL_REMOVAL_MODAL,
    GET_PENDING_RESERVATION_PRICE_DETAILS,
    GET_PENDING_RESERVATION_PRICE_DETAILS_ERROR,
    GET_RENTAL_RESERVATIONS,
    GET_RENTAL_RESERVATIONS_ERROR,
    GET_RESERVATIONS,
    GET_RESERVATIONS_ERROR,
    OPEN_PASS_CONFIRMATION_MODAL,
    PENDING_RESERVATION_NEED_TO_KNOW_REQUEST,
    PENDING_RESERVATION_PROP_CHANGE_REQUEST,
    PENDING_RESERVATION_PROP_CHANGED,
    REMOVE_RESERVATION_FROM_CART,
    REMOVE_RESERVATION_FROM_CART_ERROR,
    REPROCESS_RESERVATION_ERROR,
    REPROCESS_RESERVATION_SUCCESS,
    RESET_ORDER_DETAILS_STATE,
    SET_CAN_MODIFY_AS_ITINERARY,
    SET_GROUP_SIZE_NOTIFICATION,
    SET_ORDER_DETAILS_URL,
    SET_SAVE_CHANGES_TO_ACCOUNT,
    SET_VALIDATION_ERRORS,
    TRIGGER_IS_REMOVING,
    TRIGGER_ORDER_DETAILS_VALIDATION,
    WEB_SOURCE,
} from '../../shared/constants';
import { STANDARD_TYPE_FACILITY } from '../../shared/constants/campground';
import { applyAllFeesWaived } from '../../shared/utils/order_details';
import {
    checkIfRentalOnly,
    getUpdatedErrors,
    getViolations,
    initializePendingReservations,
    initializePriceDetails,
    updatePendingReservation,
    updateUnprocessedValidationErrors,
} from '../../shared/utils/orderDetailsReducerUtils';

const INITIALSTATE = {
    reservations: [],
    pendingReservations: {},
    canModifyAsItinerary: {},
    pendingSiteChangeReservationId: '',
    hasConsentedNeedToKnow: false,
    pendingReservationsErrors: {},
    pendingReservationsErrorsUnprocessed: {},
    remainingReservations: [],
    priceDetails: {},
    balance: 0,
    pageUrl: '',
    facilityId: '',
    reservationType: STANDARD_TYPE_FACILITY,
    isPageLoading: false,
    isPriceLoading: false,
    shouldValidate: false,
    isRentalOnly: false,
    accountToUpdate: '',
    isPassConfirmationModalOpen: false,
    isPassConfirmed: false,
    removalModalProperties: {
        isRemoving: false,
        isOpen: false,
    },
    error: null,
    priceDetailsError: null,
    violations: [],
    showGroupSizeNotification: false,
};

/**
 * calculates the balance owed between the initial and the new reservation price
 * @param {object} reservations - original reservations (will only contain 1 since this is modify-specific)
 */
const calculateBalance = (reservations, priceDetails) => {
    const reservation = get(reservations, '[0]', null);
    const reservationId = get(reservation, 'reservation_id', '');
    const totalPaid = parseFloat(get(reservation, 'total_paid', 0), 10);
    const totalNew = parseFloat(get(priceDetails[reservationId], 'total_cost', 0), 10);

    return subtract(totalNew, totalPaid);
};

/**
 * handle redirecting the page after a reservation removal and new list of
 * remaining pending reservations has been returned
 */
const compileOrderDetailsRedirectUrl = (remainingReservations, facilityId) => {
    let url = '';

    /* if there are no remaining reservations associated to
    this order details, redirect to campground page */
    if (isEmpty(remainingReservations)) {
        url = `/camping/campgrounds/${facilityId}`;
    } else {
        /* If there are remaining reservations, reconstruct the
    order details url with the updated reservation(s) id params */
        let idsStr = '';
        forEach(remainingReservations, (res) => {
            idsStr += idsStr === '' ? '?' : '&';
            idsStr += `id=${res.reservation_id}`;
        });
        url = `/camping/reservations/orderdetails${idsStr}`;
    }

    return url;
};

export default function orderDetailsReducer(state = INITIALSTATE, action) {
    const st = { ...state };

    const groupSize = window.sessionStorage.getItem('groupSize');
    const sessionGroupSize = groupSize
        ? JSON.parse(window.sessionStorage.getItem('groupSize'))
        : 0;

    switch (action.type) {
        case GET_RESERVATIONS: {
            delete st.error;
            const newReservations = get(action.payload, 'reservations', []);
            const account = get(action.payload, 'account', null);

            /* ENHANCE: directly setting this since this is only used for calculating the balance on
            the modify page for now. When we refactor the fetch reservation flow with RxJs
            this will be a non-issue */
            st.reservations = newReservations;

            st.reservationType = get(
                newReservations,
                '[0].facility_type',
                STANDARD_TYPE_FACILITY
            );
            st.facilityId = get(newReservations, '[0].facility_id', '');

            // create and merge pending campsite reservations with existing pending reservations object
            st.pendingReservations = initializePendingReservations({
                newReservations,
                pendingReservations: cloneDeep(st.pendingReservations),
                sessionGroupSize,
                reservationType: st.reservationType,
                source: WEB_SOURCE,
                account,
            });
            st.isRentalOnly = checkIfRentalOnly(st.pendingReservations);
            st.priceDetails = initializePriceDetails(st.pendingReservations);
            st.violations = getViolations(st.pendingReservations);

            return st;
        }
        case GET_RESERVATIONS_ERROR: {
            const { error } = action;
            st.error = {
                title: 'Error fetching reservations',
                content: error,
            };
            return st;
        }

        case GET_RENTAL_RESERVATIONS: {
            delete st.error;
            const newRentalReservations = get(action.payload, 'reservations', []);

            st.facilityId = get(newRentalReservations, '[0].facility_id', '');
            st.reservations = cloneDeep(newRentalReservations);

            // create and merge pending rentals reservations with existing pending reservations object
            st.pendingReservations = initializePendingReservations({
                newReservations: newRentalReservations,
                pendingReservations: cloneDeep(st.pendingReservations),
                sessionGroupSize,
                reservationType: st.reservationType,
                source: WEB_SOURCE,
            });

            st.isRentalOnly = checkIfRentalOnly(st.pendingReservations);
            st.priceDetails = initializePriceDetails(st.pendingReservations);
            st.violations = getViolations(st.pendingReservations);

            return st;
        }

        case GET_RENTAL_RESERVATIONS_ERROR:
            st.error = {
                title: 'Error fetching rental reservations',
                content: action.payload,
            };
            return st;

        case PENDING_RESERVATION_PROP_CHANGE_REQUEST: {
            const { reservationId, path, value } = action.payload;
            st.pendingReservations = updatePendingReservation({
                pendingReservations: st.pendingReservations,
                reservationId,
                path,
                value,
                reservationType: st.reservationType,
            });
            st.isPriceLoading = true;
            st.shouldValidate = false;
            return st;
        }

        case PENDING_RESERVATION_PROP_CHANGED:
            set(st, 'isPriceLoading', action.isPriceLoading);
            return st;

        case PENDING_RESERVATION_NEED_TO_KNOW_REQUEST:
            set(st, 'hasConsentedNeedToKnow', !st.hasConsentedNeedToKnow);
            st.shouldValidate = false;
            return st;

        case REPROCESS_RESERVATION_SUCCESS: {
            const { total_cost, total_paid } = action.payload;

            delete st.error;
            st.balance = total_cost - total_paid;
            st.pageUrl = '/cart';
            return st;
        }

        case REPROCESS_RESERVATION_ERROR:
            st.error = {
                title: 'Error Reprocessing Reservation',
                content: `We were not able to process your reservation at this time. Please try again. If this issue persists, please contact ${action.email}.`,
            };
            return st;

        case CLEAR_VALIDATION_ERRORS:
            st.pageUrl = '';
            st.pendingReservationsErrors = {};
            st.pendingReservationsErrorsUnprocessed = {};
            st.shouldValidate = false;
            return st;

        case SET_VALIDATION_ERRORS: {
            const { errors, resId, formKey } = action.payload;
            const updatedUnprocessedErrors = updateUnprocessedValidationErrors(
                st.pendingReservationsErrorsUnprocessed,
                errors,
                resId,
                formKey
            );
            st.pendingReservationsErrorsUnprocessed = updatedUnprocessedErrors;
            st.pendingReservationsErrors = getUpdatedErrors(
                st.pendingReservations,
                updatedUnprocessedErrors,
                st.isRentalOnly
            );
            return st;
        }

        case GET_PENDING_RESERVATION_PRICE_DETAILS: {
            const { id, payload } = action;
            const updatedPriceDetails = { ...st.priceDetails };
            updatedPriceDetails[id] = applyAllFeesWaived(
                payload,
                get(st.pendingReservations, id, null)
            );

            st.priceDetailsError = null;
            st.priceDetails = updatedPriceDetails;
            st.balance = calculateBalance(st.reservations, st.priceDetails);
            set(st, 'isPriceLoading', false);
            return st;
        }

        case GET_PENDING_RESERVATION_PRICE_DETAILS_ERROR: {
            const supplementalError = 'Invalid Supplemental Camping Quantity';
            const updatedError =
                'Your overnight camping reservation conflicts with your current Add to your Trip stay. Please update your dates in line with your overnight reservation.';

            st.priceDetailsError = {
                title: 'Error:',
                content: action.error === supplementalError ? updatedError : action.error,
            };
            set(st, 'isPriceLoading', false);
            return st;
        }

        case TRIGGER_IS_REMOVING:
            delete st.error;
            st.removalModalProperties = {
                ...st.removalModalProperties,
                isRemoving: true,
            };
            return st;

        case CONTROL_REMOVAL_MODAL: {
            const { isOpen } = action;
            st.removalModalProperties = { ...st.removalModalProperties, isOpen };
            return st;
        }

        case REMOVE_RESERVATION_FROM_CART: {
            delete st.error;

            const reservationIdToRemove = action.reservationId;
            // payload returns all pending reservations (even outside of this facility) under the user's account
            const remainingReservationsInCart = get(action, 'payload.reservations', []);

            /* filter out only the reservations that exists in the current order details page
            (this is because remainingReservationsInCart also could include pending reservations
            from other facilities made by the same user) */
            const remainingReservations = filter(
                remainingReservationsInCart,
                (cartRes) => {
                    const matchedRes = get(
                        st.pendingReservations,
                        cartRes.reservation_id,
                        null
                    );
                    return !isNil(matchedRes);
                }
            );

            const updatedPendingReservations = { ...st.pendingReservations };
            if (updatedPendingReservations[reservationIdToRemove]) {
                delete updatedPendingReservations[reservationIdToRemove];
            }
            st.pendingReservations = updatedPendingReservations;

            st.removalModalProperties = {
                ...st.removalModalProperties,
                isOpen: false,
                isRemoving: false,
            };
            st.pageUrl = compileOrderDetailsRedirectUrl(
                remainingReservations,
                st.facilityId
            );

            return st;
        }

        case REMOVE_RESERVATION_FROM_CART_ERROR: {
            set(st, 'removalModalProperties.isRemoving', false);
            set(st, 'removalModalProperties.isOpen', false);
            st.error = {
                title: 'Error removing reservation',
                content: action.error,
            };
            return st;
        }
        case SET_ORDER_DETAILS_URL:
            st.pageUrl = action.payload;
            return st;

        case OPEN_PASS_CONFIRMATION_MODAL:
            st.isPassConfirmationModalOpen = true;
            st.isPageLoading = false;
            return st;

        case CLOSE_PASS_CONFIRMATION_MODAL:
            st.isPassConfirmationModalOpen = false;
            return st;

        case CONFIRM_PASS:
            st.isPassConfirmationModalOpen = false;
            st.isPassConfirmed = true;
            st.isPageLoading = true;
            return st;

        case TRIGGER_ORDER_DETAILS_VALIDATION:
            st.shouldValidate = true;
            st.isPageLoading = true;
            return st;

        case SET_SAVE_CHANGES_TO_ACCOUNT:
            st.accountToUpdate = action.payload;
            return st;

        case COMPLETE_SUBMISSION_SUCCESS:
            st.pageUrl = '/cart';
            st.isPageLoading = false;
            return st;

        case COMPLETE_SUBMISSION_FAILURE:
            // if the action includes error, display error as a page-level error
            if (action.error) {
                const { reservation } = action;

                const reservationName = reservation.resource_id
                    ? reservation.resource_type
                    : `Site: ${reservation.campsite_name}, Loop: ${reservation.campsite_loop}`;

                st.error = {
                    title: 'Error submitting reservation',
                    content: `${reservationName}: ${action.error}`,
                };
            }
            /* if action doesn't include error, don't do anything
            (let pendingReservationsErrors state handle the rest) */
            st.isPageLoading = false;
            return st;

        case CHANGE_ORDER_DETAILS_VIEW:
            st.pendingSiteChangeReservationId = action.payload;
            return st;

        case RESET_ORDER_DETAILS_STATE:
            delete st.error;
            st.pageUrl = '';
            return st;
        case CLEAR_ORDER_DETAILS_ERROR:
            st.error = null;
            return st;
        case SET_CAN_MODIFY_AS_ITINERARY:
            st.canModifyAsItinerary = action.canModifyAsItinerary;
            return st;
        case SET_GROUP_SIZE_NOTIFICATION:
            st.showGroupSizeNotification = true;
            return st;
        case CLEAR_GROUP_SIZE_NOTIFICATION:
            st.showGroupSizeNotification = false;
            return st;
        default:
            return st;
    }
}
